<template>
  <div class="common-select">
    <el-select
      size="small"
      v-model="curSelected"
      v-loadmore="handleLoadMore"
      :placeholder="placeholder"
      :collapse-tags="isMultiple"
      :multiple="isMultiple"
      :filterable="filterable"
      :clearable="clearable"
      :remote="needRemote"
      :remote-method="handleRemoteSearch"
      @change="handleChange">
      <el-option v-if="needAll" :value="isMultiple ? 'all' : ''" :label="allText" />
      <el-option v-for="(item, index) in needRemote ? remoteInfo.list : options" :key="index.value" :label="item.label" :value="item.value" />
      <div v-if="pageLoading || pageInfo.loading" class="loading">加载中</div>
    </el-select>
  </div>
</template>

<script>
/**
 * 通用选择输入组件，支持多选、分页、远程模糊搜索
 */
import request from '@/utils/request';
import { newHost } from '@/global-config';

const mapRemoteConfig = {
  shop: { // 门店
    remoteUrl: '/shop/listPage',
    valAttr: 'shopId',
    showAttr: ['shopName', 'shopId'],
  },
};

export default {
  name: 'CommonSelect',
  props: {
    placeholder: {
      type: String,
      default: '请选择',
    },
    isMultiple: { // 是否多选
      type: Boolean,
      default: false,
    },
    options: { // 下拉选项
      type: Array,
      default: () => ([]),
    },
    selected: { // v-model中绑定显示
      type: [Array, String],
      default: '',
    },
    needAll: { // 是否可以选全部
      type: Boolean,
      default: false,
    },
    allText: { // 全部选项的文案
      type: String,
      default: '全部',
    },
    needPaging: { // 是否需要分页
      type: Boolean,
      default: false,
    },
    pageLoading: { // 是否正在加载下一页
      type: Boolean,
      default: false,
    },
    filterable: { // 是否支持搜索
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    needRemote: { // 是否需要远程模糊搜索
      type: Boolean,
      default: false,
    },
    remoteConfig: {
      type: Object,
      default: () => ({
        remoteUrl: '',
        valAttr: '',
        showAttr: '',
        quickRemoteLabel: '',
      }),
    },
  },
  model: {
    prop: 'selected',
    event: 'change',
  },
  data() {
    return {
      curSelected: this.isMultiple ? ['all'] : '',
      pageInfo: { // 分页信息
        pageNo: 1,
        pageSize: 20,
        loading: false,
        finished: false,
      },
      remoteInfo: { // 远程模糊搜索信息
        keyword: '',
        list: [],
      },
    };
  },
  watch: {
    selected: {
      handler(newVal) {
        this.curSelected = newVal;
      },
      deep: true,
    },
  },
  mounted() {
    if (this.needRemote) {
      this.handleRemoteSearch();
    }
  },
  methods: {
    // 选择变更处理
    handleChange(selected) {
      if (!this.isMultiple) {
        this.$emit('change', this.curSelected);
      } else {
        if (selected.slice(-1)[0] === 'all') { // 新选择的是全部，则变更为“全部”
          this.curSelected = ['all'];
        } else if (selected.includes('all')) { // 在选择了全部的情况下选了其他选项，去掉“全部”
          this.curSelected = this.curSelected.filter(item => item !== 'all');
        }
        this.$emit('change', this.curSelected); // v-model中绑定更改
      }
    },
    // 触底分页处理
    handleLoadMore() {
      if (this.needRemote) {
        this.handleRemoteSearch();
      } else if (this.needPaging && !this.pageLoading) {
        console.log('loadmore...');
        this.$emit('loadmore');
      }
    },
    // 远程模糊搜索处理
    handleRemoteSearch(queryString) {
      console.log('handleRemoteSearch!');
      if (queryString !== undefined) { // 输入更新关键词
        this.remoteInfo.keyword = queryString;
        this.remoteInfo.list = [];
        this.pageInfo.pageNo = 1;
      } else if (this.pageInfo.loading || this.pageInfo.finished) {
        return; // 加载下一页中或结束分页不继续请求
      }

      const { keyword } = this.remoteInfo;
      const { quickRemoteLabel, defaultSelectIndex } = this.remoteConfig;
      const { remoteUrl, showAttr, valAttr } = quickRemoteLabel ? mapRemoteConfig[quickRemoteLabel] : this.remoteConfig;
      const params = {};
      if (keyword) {
        params.input = keyword;
      }
      if (this.needPaging) {
        const { pageNo, pageSize } = this.pageInfo;
        params.pageNo = pageNo;
        params.pageSize = pageSize;
        this.pageInfo.loading = true;
      }
      request({
        url: remoteUrl,
        method: 'post',
        data: params,
        baseURL: newHost,
      }).then((res) => {
        const { code, msg, data } = res.data;
        if (String(code) === '0') {
          const newList = data.list.map(v => ({
            label: Array.isArray(showAttr) ? showAttr.map(i => v[i]).join(' ') : v[showAttr],
            value: v[valAttr],
          }));
          this.remoteInfo.list = [...this.remoteInfo.list, ...newList];
          if (this.needPaging) {
            if ('defaultSelectIndex' in this.remoteConfig && this.pageInfo.pageNo === 1) {
              this.curSelected = this.remoteInfo.list[defaultSelectIndex].value;
              this.$emit('change', this.remoteInfo.list[defaultSelectIndex].value);
            }
            if (newList.length < this.pageInfo.pageSize) {
              this.pageInfo.finished = true;
            } else {
              this.pageInfo.pageNo += 1;
            }
          }
        } else {
          this.$message.error(msg);
        }
      }).finally(() => { this.pageInfo.loading = false; });
    },
  },
};
</script>

<style rel="stylesheet/scss" lang="scss" scoped>
.loading {
  padding: 5px 0;
  font-size: 12px;
  color: #606266;
  text-align: center;
}

/deep/ .el-select__tags-text {
  width: auto;
  max-width: 100px;
}
</style>
